import { growHeightFadeIn } from '@/utils/animations';
import { DS_BREAKPOINT, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { Close } from '@mui/icons-material';
import { AudioPlayer } from '@rhg/components/media/audio-player';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import commonMessages from '@rhg/lang/messages/common';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    Dialog,
    IconButton,
    Slide,
    Stack,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { ChevronDown } from '@rhg/libs/material/icons';
import { responsivePaddingDefault, sxAppend } from '@rhg/libs/material/sx';
import { MediaEvent } from '@rhg/utils/media';
import { KeyboardEventHandler, useCallback, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { AudioPlayerContent } from '../audio-player/content';
import { AudioPlayerControls, AudioPlayerVariant } from '../audio-player/types';
import { ToggleControlsArea } from '../toggle-controls-area';
import { AudioPlayerDialogProps } from './types';

const styleUpperActionsContainer = {
    width: '100%',
    zIndex: (theme: Theme) => theme.zIndex.modal,
    position: 'absolute',
    top: 0,
    left: 0,
};
const styleUpperActions = {
    p: responsivePaddingDefault,
};
const styleUpperButton = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'background.paper'
            : theme.palette.mask.black['60p'],
    color: 'action.active',
};
const styleUpperButtonFullScreen = {
    color: 'primary.main',
};
const styleDialogBackgroundImage = {
    backgroundColor: 'background.primary',
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    backgroundPositionY: 'center',
    height: '100%',
    width: '100%',
};

const styleDialogBackground = {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.mask.white['80p']
            : theme.palette.mask.black['60p'],
};

const styleTransparentDialogBackground = {
    backgroundColor: 'transparent',
};

const stylePlayerContainer = {
    position: 'relative',
    width: '100%',
    height: '100%',
    p: responsivePaddingDefault,
};

const styleContent = { maxWidth: DS_BREAKPOINT.LARGE };

const styleClickableBackgroundRegion = {
    height: '100%',
    position: 'unset',
    top: 'unset',
    zIndex: 2,
};

const styleAudioContainer = {
    pt: 36,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateRows: '1fr minmax(auto, 100dvh) 1fr',
    justifyItems: 'center',
};

const styleBottomContainer = {
    width: '100%',
    px: {
        sm: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
};
const styleAudioPlayer = {
    width: '100%',
};

const styleHeightChange = {
    height: '0%',
    opacity: 0,
    animation: `0.3s ease-in-out forwards ${growHeightFadeIn} `,
};

const styleDialogBottomActionContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    height: '48px',
};

export function AudioPlayerDialog({
    audio,
    title,
    description,
    narrators = [],
    authors = [],
    imageSrc,
    isOpen,
    isCloseButtonDisplayed = true,
    isMinimizeButtonDisplayed = true,
    controls = AudioPlayerControls.Regular,
    autoplay,
    previousProgress,
    trackDialog,
    trackProps,
    upperActionButton,
    onMinimize,
    onProgress,
    onClose,
    onEnd,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
    dialogBottomAction,
}: AudioPlayerDialogProps) {
    const { formatMessage } = useIntl();
    const id = useId();
    const [areControlsHidden, setAreControlsHidden] = useState(false);

    const progressTracker = useCallback<MediaTrackerFn>(
        (progress) => {
            onProgress?.(progress);
            if (progress.eventType === MediaEvent.Ended) {
                onEnd?.();
            }
        },
        [onEnd, onProgress]
    );

    const toggleControlsDisplay = useCallback(
        () => setAreControlsHidden((prevState) => !prevState),
        []
    );

    const toggleControlsKeyboardListener = useCallback<
        KeyboardEventHandler<HTMLDivElement>
    >(
        ({ code }) => {
            if (code === 'Tab' || code === 'Enter' || code === 'Space') {
                return;
            }

            toggleControlsDisplay();
        },
        [toggleControlsDisplay]
    );

    return (
        <Dialog
            aria-labelledby={id}
            onClose={onClose}
            open={isOpen}
            trackDialog={trackDialog ?? 'AudioPlayer'}
            {...{ name: trackDialog ?? 'AudioPlayer' }}
            trackProps={trackProps}
            fullScreen
            onKeyDown={toggleControlsKeyboardListener}
        >
            <Box
                sx={sxAppend(
                    imageSrc && {
                        backgroundImage: `url(${imageSrc})`,
                    },
                    styleDialogBackgroundImage
                )}
            >
                <Box
                    sx={sxAppend(
                        styleDialogBackground,
                        areControlsHidden && styleTransparentDialogBackground
                    )}
                >
                    <Typography id={id} variant="srOnly">
                        {formatMessage(commonMessages['common.audio_player'])}
                    </Typography>
                    <Box sx={stylePlayerContainer}>
                        {(isCloseButtonDisplayed ||
                            upperActionButton ||
                            isMinimizeButtonDisplayed) && (
                            <Slide
                                direction="down"
                                in={!areControlsHidden}
                                mountOnEnter
                                unmountOnExit
                            >
                                <Box sx={styleUpperActionsContainer}>
                                    <Stack
                                        sx={styleUpperActions}
                                        direction="row"
                                        justifyContent="space-between"
                                    >
                                        {isMinimizeButtonDisplayed && (
                                            <Box>
                                                <IconButton
                                                    sx={sxAppend(
                                                        styleUpperButton,
                                                        styleUpperButtonFullScreen
                                                    )}
                                                    aria-label={formatMessage(
                                                        playerMessages[
                                                            'player.minimize'
                                                        ]
                                                    )}
                                                    onClick={onMinimize}
                                                    size="large"
                                                    trackButton="MinimizePlayer"
                                                >
                                                    <ChevronDown />
                                                </IconButton>
                                            </Box>
                                        )}
                                        <Box>
                                            {!!upperActionButton &&
                                                upperActionButton}
                                            {isCloseButtonDisplayed && (
                                                <IconButton
                                                    sx={sxAppend(
                                                        styleUpperButton,
                                                        styleUpperButtonFullScreen
                                                    )}
                                                    aria-label={formatMessage(
                                                        commonMessages[
                                                            'common.dismiss_dialog'
                                                        ]
                                                    )}
                                                    onClick={onClose}
                                                    size="large"
                                                    trackButton="ClosePlayer"
                                                >
                                                    <Close />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            </Slide>
                        )}
                        {!!audio && isOpen && (
                            <Box sx={styleAudioContainer}>
                                {(title ||
                                    description ||
                                    narrators.length > 0 ||
                                    authors.length > 0) && (
                                    <AudioPlayerContent
                                        sx={styleContent}
                                        title={title}
                                        description={description}
                                        narrators={narrators}
                                        authors={authors}
                                        areControlsHidden={areControlsHidden}
                                    />
                                )}
                                <ToggleControlsArea
                                    actionsCount={upperActionButton ? 2 : 1}
                                    areControlsHidden={areControlsHidden}
                                    onToggle={toggleControlsDisplay}
                                    hasDialogBottomAction={!!dialogBottomAction}
                                    sx={
                                        areControlsHidden
                                            ? undefined
                                            : styleClickableBackgroundRegion
                                    }
                                />
                                <Box
                                    sx={sxAppend(
                                        styleBottomContainer,
                                        areControlsHidden && styleHeightChange
                                    )}
                                >
                                    <AudioPlayer
                                        sx={styleAudioPlayer}
                                        audio={audio}
                                        areControlsHidden={areControlsHidden}
                                        controls={controls}
                                        variant={AudioPlayerVariant.Expanded}
                                        autoplay={autoplay}
                                        previousProgress={previousProgress}
                                        onClose={onClose}
                                        trackFn={progressTracker}
                                        loop={
                                            controls ===
                                            AudioPlayerControls.Soundscape
                                        }
                                        onNextTrack={onNextTrack}
                                        onPreviousTrack={onPreviousTrack}
                                        willTrackProgress
                                        onControlClick={onControlClick}
                                    />
                                    {dialogBottomAction &&
                                    !areControlsHidden ? (
                                        <Box
                                            sx={
                                                styleDialogBottomActionContainer
                                            }
                                        >
                                            {dialogBottomAction}
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}
